// ADMIN-INSIGHTS: Home's "Pinned answers" widget (owners and admins only): the answers pinned from
// Ask — the person's own and those shared with every admin — each drawn as it was pinned, with the
// numbers of the last scheduled refresh (hourly). Hover icons: refresh now (a live read, nothing
// saved), ask about it, unpin. Absent while nothing is pinned.
import type { OperationOutput } from "@companyos/sdk";
import { Button, InfoTip, toast } from "@companyos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircleQuestionIcon, PinOffIcon, RefreshCwIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDateTime } from "../format";
import { problemText } from "../ops-parts";
import { useAsk, widgetsKey } from "./ask-host";
import { type InsightResult, InsightView } from "./insight-view";

type Widget = OperationOutput<"insights.widgets.list">["data"][number];

export function PinnedAnswers({ api, company }: { api: Api; company: CompanyAccess }) {
  const co = company.id;
  const list = useQuery({
    queryKey: widgetsKey(co),
    queryFn: () => api.client.insights.widgets.list({ params: { co } }),
    refetchInterval: 15 * 60_000,
  });
  const widgets = list.data?.data ?? [];
  if (widgets.length === 0) return null;
  return (
    <section
      aria-label="Pinned answers"
      data-slot="home-pinned"
      className="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-3"
    >
      {widgets.map((w) => (
        <PinnedCard key={w.id} api={api} co={co} widget={w} />
      ))}
    </section>
  );
}

function PinnedCard({ api, co, widget: w }: { api: Api; co: string; widget: Widget }) {
  const qc = useQueryClient();
  const ask = useAsk();
  const [live, setLive] = useState<InsightResult | null>(null);
  const refresh = useMutation({
    mutationFn: () =>
      api.client.insights.query({
        params: { co },
        query: { measure: w.spec.measure, by: w.spec.by, period: w.spec.period },
      }),
    onSuccess: setLive,
    onError: (e) => toast.add({ title: problemText(e, "Not refreshed"), type: "error" }),
  });
  const unpin = useMutation({
    mutationFn: () => api.client.insights.widgets.delete({ params: { co, widget_id: w.id } }),
    onSuccess: () => {
      toast.add({ title: `Unpinned: ${w.title}` });
      void qc.invalidateQueries({ queryKey: widgetsKey(co) });
    },
    onError: (e) => toast.add({ title: problemText(e, "Not unpinned"), type: "error" }),
  });
  // The newer of a manual refresh and the scheduled one.
  const useLive = live !== null && (!w.refreshed_at || live.computed_at > w.refreshed_at);
  const result = useLive ? live : w.result;
  const when = useLive ? live.computed_at : w.refreshed_at;
  return (
    <article
      data-slot="pinned-answer"
      data-widget={w.id}
      className="group flex min-w-0 flex-col gap-1.5 rounded-lg border border-border bg-card px-3 py-2"
    >
      <header className="flex min-h-6 min-w-0 items-center gap-1">
        <h3 className="min-w-0 flex-1 truncate text-[13px] font-medium">{w.title}</h3>
        <span className="flex shrink-0 items-center opacity-60 group-hover:opacity-100 focus-within:opacity-100">
          <Button
            variant="ghost"
            size="icon-xs"
            aria-label={`Refresh ${w.title}`}
            title="Refresh now"
            disabled={refresh.isPending}
            onClick={() => refresh.mutate()}
          >
            <RefreshCwIcon />
          </Button>
          {ask ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Ask about ${w.title}`}
              title="Ask about this"
              onClick={() => ask(`Explain ${result?.title ?? w.title}`)}
            >
              <MessageCircleQuestionIcon />
            </Button>
          ) : null}
          {w.can_remove ? (
            <Button
              variant="ghost"
              size="icon-xs"
              aria-label={`Unpin ${w.title}`}
              title="Unpin"
              disabled={unpin.isPending}
              onClick={() => unpin.mutate()}
            >
              <PinOffIcon />
            </Button>
          ) : null}
        </span>
      </header>
      {result ? (
        <InsightView result={result} viz={w.viz} rows={4} />
      ) : (
        <span className="text-xs text-muted-foreground">Not computed yet</span>
      )}
      <footer className="flex min-w-0 items-center gap-1 text-xs text-muted-foreground">
        <span className="truncate">
          {when ? `Updated ${formatDateTime(when)}` : "Refreshes hourly"}
          {w.visibility === "admins" ? ` · shared by ${w.owner.name}` : ""}
        </span>
        {result ? (
          <InfoTip label={`About ${w.title}`}>
            {result.title}. Refreshed every hour from the company's own records.
          </InfoTip>
        ) : null}
      </footer>
    </article>
  );
}
